/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  useState,
  useEffect,
  useContext,
  useRef,
  useLayoutEffect,
  Key,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import React from 'react';
import { message, Upload } from 'antd';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';
import { useIntl } from 'react-intl';
import { queryCache, useMutation } from 'react-query';
import { debounce, uniqBy } from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';
import { GetTenderAttachmentResponse } from '~src/services/api/tender';
import { useThemeSwitcher } from 'react-css-theme-switcher';

import PropertiesPanel from '~components/atoms/PropertiesPanel/PropertiesPanel';
import CherryPickPanel from '~src/components/atoms/CherryPickPanel/CherryPickPanel';
import UploadPanel from '~components/atoms/UploadPanel/UploadPanel';
import UploadDropZonePannel from '~components/atoms/UploadDropZonePannel/UploadDropZonePannel';
import { ShareModal } from '~components/popups-modals/Share.modal';
import { FolderPropertiesModal } from '~components/popups-modals/FolderPropertiesModal/FolderPropertiesModal';
import dataContextMenu from '~src/data/dataContextMenu';
import UploadContext from '~src/contexts/UploadContext';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './style';
import AppContext from '~src/contexts/AppContext';
import {
  DbFile,
  DbFolder,
  DbFocusOptions,
  DbModuleFile,
  DbTenderFolder,
  DbAttachment,
  DbShare,
  DbTenderSVFAttachment,
} from '~src/types/db';
import { EditFolderModal } from '~components/popups-modals/EditFolder.modal';
import { MoveModal } from '~components/popups-modals/Move.modal';
import { EditFileModal } from '~components/popups-modals/EditFile.modal';
import { DeleteModal } from '~components/popups-modals/Delete.modal';
import {
  downloadAttachment,
  downloadFileLastVersion,
  downloadFolder,
  downloadModuleFile,
  downloadTenderFolder,
  downloadTenderFolderMultipleItems,
  downloadUnassignedTenderFile,
  downloadUnassignedTenderFolder,
  downloadVersionedFile,
  downloadVersionedFolderFile,
} from '~src/utils/download';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { appLog } from '~src/utils/log';
import LargeFilesContext from '~src/contexts/LargeFilesContext';
import {
  FileTable,
  FileTableProps,
} from '~components/atoms/FileTables/FileTable';
import {
  handleSelectCtrl,
  MovementEntry,
  scrollToSelectedItem,
  TableEntry,
} from '~components/atoms/FileTables/FileTable.utils';
import { RecentsTable } from '~components/atoms/FileTables/RecentsTable';
import { RightsModal } from '~components/popups-modals/RightsModal/RightsModal';
import { FavouritesTable } from '~components/atoms/FileTables/FavouritesTable';
import { SearchTable } from '~components/atoms/FileTables/SearchTable';
import {
  addToFavoriteFiles,
  removeFromFavoriteFiles,
  requestFileLocation,
  setPrimarySVFRender,
  convertToTender,
} from '~src/services/api';
import { updateCachedFile } from '~src/services/cache/files';
import { useToggleFavouriteFolder } from '~src/hooks/actions/useToggleFavouriteFolder';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { RightClickOverlay } from '~components/atoms/RightClickOverlay/RightClickOverlay';
import { useItemPermission } from '~src/hooks/useItemPermission';
import { useIsProjectEmpty } from '~src/hooks/useIsProjectEmpty';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
// COMMENTED OUT: Empty state now handled in FileTable
// import { EmptyProjectPannel } from '~components/atoms/EmptyprojectPannel/EmptyProjectPannel';
import ShowUploadDropZoneContext from '~src/contexts/ShowUploadDropZone';
import { useIsPath } from '~src/hooks/useIsPath';
import { ModuleTable } from '~components/atoms/FileTables/ModuleTable';
import { SectorRegistrationsTable } from '~components/atoms/FileTables/SectorRegistrationsTable';
import { useIsModuleFileAdminStatus } from '~src/hooks/useIsModuleFileAdmin';
import { ItemLogModal } from '~components/popups-modals/ItemLog.modal';
import { CreateFolderModal } from '~components/popups-modals/CreateFolder.modal';
import { UploadProps } from 'antd/lib/upload';
import { FileX } from '~src/types/upload';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { parseFileName } from '~src/utils/file';
import Viewer from '~components/atoms/Viewers/Viewer';
import {
  getLinkModuleFile,
  downloadClassicRegistrationReport,
  ClassicRegistration,
} from '~src/services/api/modules';
import { useToggleFocusFolder } from '~src/hooks/actions/useToggleFocusFolder';
import { FileProgressPanelDownload } from '~components/molecules/FileProgressPanelDownload/FileProgressPanelDownload';
import { DownloadingState, useStore } from '~src/store';
import { Modal } from 'antd';
import { saveAs } from 'file-saver';
import {
  DEFULT_PROJECT_TENDER_TAB_KEY,
  SelectedShareKeysProps,
  TenderTabs,
} from '~components/molecules/Tabs/TenderTabs/TenderTabs';
import { TenderPropertiesTable } from '~components/atoms/FileTables/TenderPropertiesTable';
import { RestoreAttachments } from '~components/popups-modals/RestoreAttachments.modal';
import { TenderUnassignedFolders } from '~components/atoms/FileTables/TenderUnassignedFolders';
import RightSideViewerContainer from '~components/atoms/Viewers/RightSideViewerContainer/RightSideViewerContainer';
import { useRightSideViewState } from '~components/molecules/RightSideView/RightSideView.state';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';
import { CorrelationLetterModal } from '~components/popups-modals/MovementsModals/CorrelationLetter.modal';
import { SVFViewerModal } from '~components/popups-modals/SVFViewer.modal';
import { SVFViewer } from '~components/atoms/Viewers/SVFViewer/SVFViewer';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { ImportTemplateModal } from '~components/popups-modals/ImportTemplateModal/ImportTemplateModal';
import { EditAttachmentModal } from '~components/popups-modals/editAttachment.modal';
import { apiCweb } from '~src/services/api/api-cweb';

export interface GetLinkModuleFileParams {
  projectId: string;
  moduleFileId: string;
  moduleId: number;
  response_type?: 'stream' | 'base64' | 'content';
}

interface RightSideViewProps {
  focusOptions?: DbFocusOptions[];
  isUnassignedItemsVisible?: boolean;
  setIsUnassignedItemsVisible?: Dispatch<SetStateAction<boolean>>;
  selectedForDetails: TableEntry | undefined;
  setSelectedForDetails: Dispatch<SetStateAction<TableEntry | undefined>>;
  selectedItems: TableEntry[];
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
}

const RightSideView: React.FC<RightSideViewProps> = ({
  focusOptions,
  isUnassignedItemsVisible,
  setIsUnassignedItemsVisible,
  selectedForDetails,
  setSelectedForDetails,
  selectedItems,
  setSelectedItems,
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();
  const { switcher, themes } = useThemeSwitcher();
  const { setAppContextState } = useContext(AppContext);
  const archiveSettings = useArchiveSettings();
  const isTenderDocumentsRoot = useRouteMatch({
    path: '/projects/:projectId/tenders/:tenderId/documents',
    exact: true,
  });
  const {
    projectId,
    tenderId,
    folderId,
    isFirstLevel,
    currentFolder,
    permissionLevel,
    isSuperuser,
    isProjectEmpty,
    isOnArchive,
    isOnTender,
    isOnShares,
    isOnMovements,
    isOnOffers,
    isOnDetails,
    isOnContract,
    isOnQA,
    modals,
  } = useRightSideViewState();

  // `module_admins` requires superuser-level access on the backend - never
  // fire it for non-superusers (e.g. Manager accounts), who would always
  // get a 403 and default to the old module nav anyway.
  const {
    isModuleAdmin,
    isLoading: isModuleAdminLoading,
  } = useIsModuleFileAdminStatus({ enabled: isSuperuser });

  const { uploadContext } = useContext(UploadContext);
  const [svfModalForceViewer, setSVFModalForceViewer] = useState(false);
  const { fileToView, viewerTopMargin, setFileToView } = useFileViewerContext();
  const [rightClickEntityOverlay, setRightClickEntityOverlay] = useState<{
    folder?: DbFolder;
    file?: DbFile;
    moduleFile?: DbModuleFile;
    noFiles?: boolean;
  }>({});

  const { setLargeFilesContext } = useContext(LargeFilesContext);
  const foldersQuery = useFolders(
    {
      before:
        window.sessionStorage.getItem('timeMachineDate') !== null
          ? Number(window.sessionStorage.getItem('timeMachineDate'))
          : undefined,
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { showUploadZone, setShowUploadZone } = useContext(
    ShowUploadDropZoneContext
  );

  const { setUploadContext } = useContext(UploadContext);
  const { toggleFavouriteFolder } = useToggleFavouriteFolder();
  const { toggleFocusFolder } = useToggleFocusFolder();
  const [doConvertToTender, {}] = useMutation(convertToTender, {
    onSuccess: () => {
      queryCache.invalidateQueries('folders');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
      appLog('ConvertToTender -> convertTender -> item', item);
      appLog('ConvertToTender -> convertTender -> err', error);
    },
  });

  // Utility function to properly invalidate queries with correct structure
  const invalidateCurrentQueries = () => {
    // Invalidate the specific files query for the current project and folder
    queryCache.invalidateQueries([
      'files',
      {
        projectId: String(projectId),
        folderId: currentFolder?.id,
        withVersions: true,
        showDeleted: false,
      },
    ]);

    // Also invalidate file versions to ensure SVF model state updates
    queryCache.invalidateQueries([
      'versions',
      {
        projectId: String(projectId),
        folderId: currentFolder?.id,
      },
    ]);

    // Invalidate folders if needed
    queryCache.invalidateQueries([
      'folders',
      {
        projectId: String(projectId),
        as_admin:
          window.sessionStorage.getItem('appState') === 'superUser'
            ? true
            : undefined,
      },
    ]);
  };
  const uploadRef = useRef<HTMLSpanElement>(null);
  const { currentFolderId, rootFolderId } = useTenderParamsFromUrl();

  const [fileRevision, setFileRevision] = useState<{
    id?: string;
    revision?: number | null;
  }>({
    id: undefined,
    revision: null,
  });
  /** Bottom view (dropZone) height, to cut from table maxHeight */
  const [bottomViewHeight, setBottomViewHeight] = useState(0);
  /** cross folder selected table entries, has to be fully custom on top of Table selection */
  // const [selectedItems, setSelectedItems] = useState<TableEntry[]>([]);
  const [selectedSharesItems, setSelectedSharesItems] = useState<DbShare[]>([]);
  const zoneDivRef = useRef<HTMLDivElement>(null);
  // const whack = useRef(debounce(setBottomViewHeight, 10));
  const resizeObserverRef = useRef(
    new ResizeObserver(
      debounce(([resizeEntry]) => {
        // update `bottomViewHeight` based on "zoneDiv" height
        setBottomViewHeight(resizeEntry.contentRect.height);
      }, 50)
    )
  );

  // const [selectedForDetails, setSelectedForDetails] = useState<TableEntry>();
  const [selectedUrl, setSelectedUrl] = useState<string | undefined>();

  // ==ImportTemplate modal state
  const [importTemplateModalVisible, setImportTemplateModalVisible] = useState(
    false
  );

  // ==SVF modal state
  // `svfModelFileToView` may be a regular archive file or a tender root SVF source.
  const [svfModal, setSVFModal] = useState(false);
  const [svfModelFileToView, setSVFModalFileToView] = useState<
    DbFile | DbModuleFile | DbTenderSVFAttachment | undefined
  >(undefined);

  // ==tender root attachment SVF state bridge
  // Root tender attachments do not update through FileVersionsContext,
  // so RightSideView owns their live state for:
  // 1) root table icon color
  // 2) modal reopen state
  // 3) background polling after modal close
  const [
    tenderAttachmentSVFStateById,
    setTenderAttachmentSVFStateById,
  ] = useState<Record<string, number | undefined>>({});

  // ==used by SVF modal + background polling
  // Keeps one current state per tender root attachment guid.
  const handleTenderAttachmentSVFStateChange = useCallback(
    (fileGuid: string, nextState: number) => {
      setTenderAttachmentSVFStateById((prev) => ({
        ...prev,
        [fileGuid]: nextState,
      }));
    },
    []
  );

  // ==tender root attachment SVF helpers
  // Keep all root-attachment specific checks in one place,
  // so modal/viewer callbacks do not repeat sourceType logic inline.
  const isTenderRootSVFSource = (
    file?: DbFile | DbModuleFile | DbTenderSVFAttachment
  ): file is DbTenderSVFAttachment => {
    return !!file && (file as any)?.sourceType === 'tender-admin-root-svf';
  };

  const getTenderRootAttachmentFinalState = (
    file?: DbFile | DbModuleFile | DbTenderSVFAttachment
  ) => {
    if (!isTenderRootSVFSource(file) || !file?.id) {
      return undefined;
    }

    return (
      tenderAttachmentSVFStateById[String(file.id)] ??
      (file as any)?.extension_svf_model?.state
    );
  };

  // ==background polling for tender root attachment SVF state
  // If user closes the modal while conversion is running, we still want the icon
  // to move from blue -> green automatically, just like archive flow does.
  useEffect(() => {
    if (!projectId || !tenderId) return;

    const activeIds = Object.entries(tenderAttachmentSVFStateById)
      .filter(([, state]) => [1, 2, 4].includes(Number(state)))
      .map(([fileGuid]) => fileGuid);

    if (!activeIds.length) return;

    let cancelled = false;

    const pollOnce = async () => {
      try {
        await Promise.all(
          activeIds.map(async (fileGuid) => {
            try {
              const res = await apiCweb.get(
                `/projects/${projectId}/tenders/${tenderId}/svf_viewer_attachments/${fileGuid}/transcribe_status`,
                {
                  withCredentials: false,
                  validateStatus: () => true,
                }
              );

              if (cancelled) return;

              // backend race: job not visible yet
              if (res.status === 404) return;

              if (res.status >= 200 && res.status < 300) {
                const nextState = res.data?.svf_model_state;

                if (typeof nextState === 'number') {
                  setTenderAttachmentSVFStateById((prev) => {
                    if (prev[fileGuid] === nextState) return prev;
                    return {
                      ...prev,
                      [fileGuid]: nextState,
                    };
                  });
                }
              }
            } catch {
              // silent retry on next tick
            }
          })
        );
      } catch {
        // silent retry on next tick
      }
    };

    // poll immediately, then every 5s while any attachment is in progress
    pollOnce();
    const intervalId = window.setInterval(pollOnce, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [projectId, tenderId, tenderAttachmentSVFStateById]);

  const [rightClickEntity, setRightClickEntity] = useState<{
    folder?: DbFolder;
    file?: DbFile;
    moduleFile?: DbModuleFile;
    tenderFolder?: DbTenderFolder;
    attachment?: DbAttachment;
    deletedAttachment?: DbAttachment;
    movement?: MovementEntry;
    classicRegistration?: ClassicRegistration;
    noFiles?: boolean;
  }>({});
  const rightClickEntityParentPermission = useItemPermission({
    folder: rightClickEntity.folder,
    file: rightClickEntity.file,
  });

  const clickTimeoutRef = useRef<number>();

  // reset selectedForDetails on navigation or file upload
  useLayoutEffect(() => {
    setSelectedForDetails(undefined);
  }, [history.location, uploadContext.length]);

  // reset selection on file upload
  useLayoutEffect(() => {
    setSelectedItems([]);
  }, [uploadContext.length]);

  // scroll to `selectedForDetails` item
  useEffect(() => {
    if (selectedForDetails?.key) {
      scrollToSelectedItem(selectedForDetails.key);
    }
  }, [selectedForDetails?.key]);

  useEffect(() => {
    // disable drag&drop if on root, or no upload rights
    if (
      !isSuperuser &&
      folderId !== null &&
      permissionLevel < RIGHTS_LEVEL.UPLOAD
    )
      return;

    const handleDragEnter = (e: DragEvent) => {
      e.stopImmediatePropagation();
      if (!showUploadZone) {
        // show drop target, when dragging file into window

        setShowUploadZone(true);
      }
    };

    document.addEventListener('dragenter', handleDragEnter);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
    };
  }, [folderId, permissionLevel, showUploadZone]);

  /** Toggle `is_favourite` prop, for selected resource */
  const toggleFavourite = async () => {
    if (rightClickEntity.file) {
      const fileId = rightClickEntity.file.id;
      const nextIsFavourite = !rightClickEntity.file.is_favorite;

      try {
        if (nextIsFavourite) {
          await addToFavoriteFiles({
            data: {
              ...rightClickEntity.file,
              is_favorite: nextIsFavourite,
            },
          });
        } else {
          await removeFromFavoriteFiles({
            data: {
              ...rightClickEntity.file,
              is_favorite: nextIsFavourite,
            },
          });
        }

        // to void dropdown stutter
        setTimeout(() => {
          // show immediate UI update
          updateCachedFile(
            fileId,
            { is_favorite: nextIsFavourite },
            { projectId: String(projectId), withVersions: true }
          );
          invalidateCurrentQueries();
        }, 200);
      } catch (err) {
        nextIsFavourite
          ? appLog('RightSideView -> addToFavoriteFiles -> err', err)
          : appLog('RightSideView -> removeFromFavoriteFiles -> err', err);
        message.error(intl.formatMessage({ id: 'favourite-failed' }));
      }
    } else if (rightClickEntity.folder) {
      toggleFavouriteFolder(rightClickEntity.folder);
    }
  };

  const { changeDownloadingActive, changeDownloadingState } = useStore();

  const rightClickAction = (modal: string, option?: number) => {
    // set modal visible
    if (modals.modalsFunc[modal]) {
      modals.modalsFunc[modal](true);
    } else {
      // non-modal actions
      if (modal === 'file-download') {
        const file = rightClickEntity.file || rightClickEntity.attachment;
        if (!file) return;

        // UNASSIGNED tender folders file download logic
        if (
          history.location.pathname.includes('/tender-folders/') &&
          !history.location.pathname.includes('/tenders/')
        ) {
          const match = history.location.pathname.match(
            /\/tender-folders\/(\d+)(?:\/folders\/(\d+))?/
          );
          const rFolderId = match ? match[1] : rootFolderId;
          const fFolderId = match && match[2] ? match[2] : rFolderId;

          if (!file || !file.id) {
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
            return;
          }

          const selectedRevision = fileVersionsContext[file.id]?.revision;
          let targetFileDetail = file?.files?.find(
            (f: any) => f.serial_number === selectedRevision
          );
          // latest serial
          if (!targetFileDetail && file?.files?.length) {
            targetFileDetail = file.files.reduce((prev: any, current: any) => {
              const prevSerial = prev.serial_number || 0;
              const currSerial = current.serial_number || 0;
              return currSerial > prevSerial ? current : prev;
            });
          }

          const regId = targetFileDetail?.registration_id || file.id;
          const serialNum = targetFileDetail?.serial_number || 1;

          return downloadUnassignedTenderFile(
            Number(projectId),
            Number(rFolderId),
            Number(fFolderId || rFolderId),
            regId,
            serialNum,
            () => message.info(intl.formatMessage({ id: 'download-starting' }))
          ).catch((err) => {
            appLog('downloadFile -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        }

        if (!isFirstLevel && 'files' in file) {
          const selectedRevision = fileVersionsContext[file.id]?.revision;

          let targetFileDetail = file?.files?.find(
            (f: any) => f.serial_number === selectedRevision
          );
          // latest serial
          if (!targetFileDetail && file?.files?.length) {
            targetFileDetail = file.files.reduce((prev: any, current: any) => {
              const prevSerial = prev.serial_number || 0;
              const currSerial = current.serial_number || 0;
              return currSerial > prevSerial ? current : prev;
            });
          }

          return downloadVersionedFolderFile(
            Number(file.project_id),
            Number(tenderId),
            Number(rootFolderId),
            Number(currentFolderId),
            targetFileDetail?.registration_id,
            targetFileDetail?.serial_number,
            () => {
              // message.info('Download started')
            }
          ).catch((err) => {
            appLog('downloadFile -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        } else if ('versions' in file) {
          return downloadFileLastVersion(file, fileVersionsContext, () =>
            message.info(intl.formatMessage({ id: 'download-starting' }))
          ).catch((err) => {
            appLog('downloadFile -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        } else {
          // return downloadFileLastVersion(
          //   file,
          //   fileVersionsContext[file.id]?.id,
          //   () => message.info(intl.formatMessage({ id: 'download-starting' }))
          // ).catch((err) => {
          //   appLog('downloadFile -> err', err);
          //   message.error(intl.formatMessage({ id: 'file-download-failed' }));
          // });
          return downloadAttachment(
            file as DbAttachment,
            file.project_id,
            String(tenderId),
            () => message.info(intl.formatMessage({ id: 'download-starting' }))
          ).catch((err) => {
            appLog('downloadFile -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        }
      }

      if (modal === 'folder-download') {
        if (!rightClickEntity.folder && !rightClickEntity.tenderFolder) return;

        // activate download progress panel
        changeDownloadingActive(true);
        changeDownloadingState(DownloadingState.PREPARING);

        const isUnassigned =
          history.location.pathname.includes('/tender-folders/') &&
          !history.location.pathname.includes('/tenders/');

        const isTenderSubfolderView =
          history.location.pathname.includes('/tenders/') &&
          history.location.pathname.includes('/tender-folders/');

        // UNASSIGNED tender folders download
        if (isUnassigned) {
          const match = history.location.pathname.match(
            /\/tender-folders\/(\d+)(?:\/folders\/(\d+))?/
          );

          return downloadUnassignedTenderFolder({
            folder: rightClickEntity.folder || rightClickEntity.tenderFolder,
            allFolders: foldersQuery.data || [],
            fileVersionsContext,
            onDownloadStarted: () => {
              setTimeout(
                () =>
                  message.info(intl.formatMessage({ id: 'download-starting' })),
                400
              );
            },
          }).catch((err) => {
            appLog('downloadFolder -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        }

        if (rightClickEntity.tenderFolder) {
          // ROOT & SUBFOLDER: Use new unified downloader for all tender folders
          return downloadTenderFolderMultipleItems({
            files: [],
            folders: [rightClickEntity.tenderFolder as any],
            allFolders: (foldersQuery.data || []) as any,
            projectId: Number(projectId),
            tenderId: Number(tenderId),
            rootFolderId: Number(rootFolderId),
            currentFolderId: Number(currentFolderId),
            fileVersionsContext,
            onDownloadStarted: () => {
              setTimeout(
                () =>
                  message.info(intl.formatMessage({ id: 'download-starting' })),
                400
              );
            },
          }).catch((err) => {
            appLog('downloadTenderFolderMultipleItems -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        }

        // ARCHIVE FOLDER
        return downloadFolder({
          folder: rightClickEntity.folder!, // Non-null assertion since we checked above
          allFolders: foldersQuery.data || [],
          fileVersionsContext,
          onDownloadStarted: () => {
            // hidePrepMsg();
            setTimeout(
              () =>
                message.info(intl.formatMessage({ id: 'download-starting' })),
              400
            );
          },
          onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
        }).catch((err) => {
          appLog('downloadFolder -> err', err);
          // hidePrepMsg && hidePrepMsg();
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      if (modal === 'favourite') {
        toggleFavourite();
      }

      if (modal === 'upload-files') {
        uploadRef.current?.click();
      }

      if (modal === 'module-file-download' && rightClickEntity.moduleFile) {
        return downloadModuleFile(rightClickEntity.moduleFile, () =>
          message.info(intl.formatMessage({ id: 'download-starting' }))
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      if (
        modal === 'module-file-download' &&
        rightClickEntity.classicRegistration
      ) {
        const reg = rightClickEntity.classicRegistration;
        try {
          message.info(intl.formatMessage({ id: 'download-starting' }));
          downloadClassicRegistrationReport({
            projectId: String(reg.project_id),
            id: reg.id,
            response_type: 'stream',
          })
            .then((blob) => {
              saveAs(blob, reg.filename || `registration_${reg.id}.pdf`);
            })
            .catch((err) => {
              appLog('downloadClassicRegistration error', err);
              message.error(intl.formatMessage({ id: 'file-download-failed' }));
            });
        } catch (err) {
          appLog('downloadClassicRegistration error', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        }
        return;
      }

      if (modal === 'folder-focus') {
        if (!rightClickEntity.folder || !option) return;
        toggleFocusFolder(rightClickEntity.folder, option);
      }

      if (modal === 'folder-tender') {
        if (!rightClickEntity.folder) return;
        doConvertToTender({
          projectId: String(projectId),
          folderId: rightClickEntity.folder.id,
        });
      }
      if (modal === 'svf-view-file') {
        if (!rightClickEntity.file) return;

        if (option === 2) {
          // Window mode
          setFileToView({ ...rightClickEntity.file });
          return;
        }
        if (option === 3) {
          // appLog(`fullscreen btn pressed!`, { ...rightClickEntity.file });
          setSVFModalFileToView(rightClickEntity.file);
          setSVFModal(true);
          setSVFModalForceViewer(true);
          return;
        }

        if (option === 4) {
          // set preview 3d file
          if (!rightClickEntity.file || !projectId) return;

          const versions = rightClickEntity.file.versions ?? [];
          const latestReadyRevision = versions.length
            ? versions[versions.length - 1]
            : null;

          if (
            !latestReadyRevision ||
            latestReadyRevision.extension_svf_model?.state !== 7
          ) {
            message.error(
              intl.formatMessage({ id: 'set-primary-preview-failed' }) ||
                'This file is not ready to be set as primary preview'
            );
            return;
          }

          setPrimarySVFRender(
            Number(rightClickEntity.file.project_id ?? projectId),
            Number(rightClickEntity.file.folder_id),
            Number(rightClickEntity.file.id),
            isSuperuser ? true : undefined
          )
            .then(() => {
              message.success(
                intl.formatMessage({ id: 'set-primary-preview-success' }) ||
                  'Primary 3D preview updated'
              );

              invalidateCurrentQueries();
              archiveSettings.refetch?.();
            })
            .catch((err) => {
              appLog('setPrimarySVFRender -> error', err);
              message.error(
                intl.formatMessage({ id: 'set-primary-preview-failed' }) ||
                  'Failed to set primary 3D preview'
              );
            });
          return;
        }

        setSVFModalFileToView(rightClickEntity.file);
        setSVFModal(true);
        setFileToView(undefined);
        if (!rightClickEntity.file) return;
      }

      if (modal === 'edit-attachment') {
        if (!rightClickEntity.attachment) return;
        // Вмикаємо модалку через модальний контекст
        // припускаю, що у modals вже є сетер; якщо ні —див. пункт 4.4 (модалки у стейті)
        modals.setAttachmentEditModal?.(true);
        return;
      }
    }
  };

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    setUploadContext((prevFiles) => {
      const file = info.fileList.find((v) => v.uid === info.file.uid)
        ?.originFileObj as FileX | undefined;
      if (!file) return prevFiles;

      return uniqBy(
        [file, ...prevFiles],
        (v) =>
          `${v.name}@${
            v.webkitRelativePath === v.name ? '' : v.webkitRelativePath
          }`
      );
    });
  };

  /**
   * Row right-click handler
   *
   * sets rightClickEntity for context menu
   */
  const handleSelectedFileContext = useCallback(
    (record: TableEntry | undefined) => {
      if (record) {
        if (record.dbModuleFile) {
          setRightClickEntity({ moduleFile: record.dbModuleFile });
        } else if (record.folder && record.dbFolder) {
          setRightClickEntity({ folder: record.dbFolder });
        } else if (record.dbTenderFolder) {
          setRightClickEntity({ tenderFolder: record.dbTenderFolder });
        } else if (record.dbAttachment) {
          setRightClickEntity({ attachment: record.dbAttachment });
        } else if (record.dbFile && isOnTender) {
          // If we're on a tender page, treat the file as an attachment
          setRightClickEntity({ attachment: record.dbFile });
        } else if (record && isOnMovements) {
          setRightClickEntity({ movement: record });
        } else if (record) {
          setRightClickEntity({ file: record.dbFile });
          setRightClickEntityOverlay({ file: record.dbFile });
        }
        setSelectedForDetails(record);
      } else {
        setRightClickEntity({ noFiles: true });
        setRightClickEntityOverlay({ noFiles: true });
      }
    },
    [isOnTender, isOnMovements, setSelectedForDetails]
  );

  const toggleItemSelected = useCallback(
    (item: TableEntry) => {
      setSelectedItems((prevItems) => {
        const nextItems = [...prevItems];
        const index = nextItems.findIndex((v) => v.key === item.key);

        if (index > -1) {
          // found -> remove item
          nextItems.splice(index, 1);
        } else {
          nextItems.unshift(item); // insert at start of array
        }

        return nextItems;
      });
    },
    [setSelectedItems]
  );

  const handleFolderDoubleClick = useCallback(
    (record: TableEntry) => {
      // window.clearTimeout(clickTimeoutRef.current);
      // clickTimeoutRef.current = undefined;
      history.push(
        `/projects/${projectId}/${
          record?.dbTender
            ? `tenders/${record.key}/${DEFULT_PROJECT_TENDER_TAB_KEY}`
            : `archive/${record.key}`
        }`
      );
    },
    [history, projectId]
  );

  const handleDocumentsFolderDoubleClick = useCallback(
    (record: TableEntry) => {
      const tenderIdMatch = history?.location?.pathname?.match(
        /tenders\/(\d+)/
      );
      const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';
      // window.clearTimeout(clickTimeoutRef.current);
      // clickTimeoutRef.current = undefined;
      if (record?.isUnassigned) {
        history.push(
          `/projects/${projectId}/tender-folders/${record.firstElementId}/folders/${record.id}`
        );
      } else if (
        record.isOnTenderPropertyPanel &&
        record.dbTenderFolder?.options.tender_registration_id === null
      ) {
        history.push(
          `/projects/${projectId}/tender-folders/${record.id}/folders/${record.id}`
        );
      } else {
        history.push(
          `/projects/${projectId}/tenders/${
            record?.dbTenderFolder?.options?.tender_registration_id || tenderId
          }/documents/tender-folders/${
            record?.dbTenderFolder?.options?.is_tender_root_folder
              ? record.id
              : record?.firstElementId
              ? record.firstElementId
              : record.dbTenderFolder?.parent_id
          }/folders/${record.id}`
        );
      }
    },
    [history, projectId]
  );

  const handleFileDoubleClick = useCallback(
    (record: TableEntry) => {
      // Check classicRegistration FIRST to avoid any edge cases
      if ((record as any).classicRegistration) {
        const reg = (record as any).classicRegistration;
        message.info(intl.formatMessage({ id: 'download-starting' }));

        downloadClassicRegistrationReport({
          projectId: String(reg.project_id),
          id: reg.id,
          response_type: 'stream',
        })
          .then((blob) => {
            saveAs(blob, reg.filename || `registration_${reg.id}.pdf`);
          })
          .catch((err) => {
            appLog('downloadClassicRegistration error', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        return;
      }

      const isUnassigned =
        history.location.pathname.includes('/tender-folders/') &&
        !history.location.pathname.includes('/tenders/');
      const isTenderFolder =
        history.location.pathname.includes('/tenders/') &&
        history.location.pathname.includes('/tender-folders/');

      if (record.dbFile) {
        const file = record.dbFile;

        if (isUnassigned) {
          const match = history.location.pathname.match(
            /\/tender-folders\/(\d+)(?:\/folders\/(\d+))?/
          );
          const rFolderId = match ? match[1] : rootFolderId;
          const fFolderId = match && match[2] ? match[2] : rFolderId;

          const file = record.dbFile;
          const selectedRevision = fileVersionsContext[file.id]?.revision;
          let targetFileDetail = file?.files?.find(
            (f: any) => f.serial_number === selectedRevision
          );
          // latest serial
          if (!targetFileDetail && file?.files?.length) {
            targetFileDetail = file.files.reduce((prev: any, current: any) => {
              const prevSerial = prev.serial_number || 0;
              const currSerial = current.serial_number || 0;
              return currSerial > prevSerial ? current : prev;
            });
          }

          const regId = targetFileDetail?.registration_id || file.id;
          const serialNum = targetFileDetail?.serial_number || 1;

          downloadUnassignedTenderFile(
            Number(projectId),
            Number(rFolderId),
            Number(fFolderId || rFolderId),
            regId,
            serialNum,
            () => message.info(intl.formatMessage({ id: 'download-starting' }))
          ).catch((err) => {
            appLog('downloadFile -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });

          return;
        }

        if (isTenderFolder && 'files' in file) {
          const selectedRevision = fileVersionsContext[file.id]?.revision;

          let targetFileDetail = file?.files?.find(
            (f: any) => f.serial_number === selectedRevision
          );
          if (!targetFileDetail && file?.files?.length) {
            targetFileDetail = file.files.reduce((prev: any, current: any) => {
              const prevSerial = prev.serial_number || 0;
              const currSerial = current.serial_number || 0;
              return currSerial > prevSerial ? current : prev;
            });
          }

          downloadVersionedFolderFile(
            Number(file.project_id),
            Number(tenderId),
            Number(rootFolderId),
            Number(currentFolderId),
            targetFileDetail?.registration_id || file.id,
            targetFileDetail?.serial_number || 1,
            () => {
              message.info(intl.formatMessage({ id: 'download-starting' }));
            }
          ).catch((err) => {
            appLog('downloadFile -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });

          return;
        }

        downloadFileLastVersion(record.dbFile, fileVersionsContext, () =>
          message.info(intl.formatMessage({ id: 'download-starting' }))
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      if (record.dbAttachment) {
        const tenderIdMatch = window?.location?.pathname?.match(
          /tenders\/(\d+)/
        );
        const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';
        downloadAttachment(record.dbAttachment, projectId, tenderId, () =>
          message.info(intl.formatMessage({ id: 'download-starting' }))
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      if (record.moduleFile && record.dbModuleFile) {
        downloadModuleFile(record.dbModuleFile, () =>
          message.info(intl.formatMessage({ id: 'download-starting' }))
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }
    },
    [fileVersionsContext, intl, projectId, history]
  );

  const handleRowClick = useCallback(
    (record: TableEntry) => {
      // if (clickTimeoutRef.current) return;
      // clickTimeoutRef.current = window.setTimeout(() => {
      // clickTimeoutRef.current = undefined;

      if (!selectedItems.length) {
        return setSelectedForDetails((prevRecord) => {
          if (prevRecord?.key !== record.key) {
            setIsUnassignedItemsVisible?.(false);
          }

          return prevRecord?.key === record.key ? undefined : record;
        });
      }

      if (!record.disabled) {
        toggleItemSelected(record);
      }
      // }, 300);
    },
    [
      selectedItems.length,
      setIsUnassignedItemsVisible,
      setSelectedForDetails,
      toggleItemSelected,
    ]
  );

  const handleShareKeyTableRowClick = (record: DbShare) => {
    // if (!selectedItems.length) {
    //   return setSelectedForDetails((prevRecord) =>
    //     prevRecord?.key === record.id ? undefined : record
    //   );
    // }
    // toggleItemSelected(record);
  };

  /** Custom table `onSelect` callback; handles toggling global selection (cross-folder)  */
  const handleSelect: Required<FileTableProps>['onSelect'] = useCallback(
    (record, selected) => {
      if (selectedForDetails) setSelectedForDetails(undefined);

      if (selected) {
        // insert item at start of arr
        setSelectedItems((prevSelectedItems) => {
          if (!prevSelectedItems.length) {
            // delay as we are within setState
            setTimeout(() => scrollToSelectedItem(record.key), 200);
          }

          return [record, ...prevSelectedItems];
        });
      } else {
        // remove item from arr
        setSelectedItems((prevSelectedItems) =>
          prevSelectedItems.filter((item) => item.key !== record.key)
        );
      }
    },
    [selectedForDetails, setSelectedForDetails, setSelectedItems]
  );

  /** Custom table `onSelectAll` callback; handles toggling global selection (cross-folder)  */
  const handleSelectAll: Required<FileTableProps>['onSelectAll'] = useCallback(
    (selected, rows, changedRows) => {
      if (selectedForDetails) setSelectedForDetails(undefined);

      // keep truthy values, because selected rows from other "folders" are included as `undefined` in the list
      const selectedRows = rows.filter(Boolean);

      if (selected) {
        // insert all items at start of arr, filter unique
        setSelectedItems((prevSelectedItems) =>
          uniqBy([...selectedRows, ...prevSelectedItems], 'key')
        );
      } else {
        // use `changedRows`, because `rows` is empty on "deselectAll"
        const rowKeys = changedRows.map((row) => row.key);
        // remove items from arr
        setSelectedItems((prevSelectedItems) =>
          prevSelectedItems.filter((item) => !rowKeys.includes(item.key))
        );
      }
    },
    [selectedForDetails, setSelectedForDetails, setSelectedItems]
  );

  /** Custom table `onSelect` callback; handles toggling global selection (cross-folder)  */
  const handleSelectShareKeys: Required<SelectedShareKeysProps>['onSelectSharedKeys'] = (
    record,
    selected
  ) => {
    if (selectedForDetails) setSelectedForDetails(undefined);

    if (selected) {
      // insert item at start of arr
      setSelectedSharesItems((prevSelectedItems) => {
        if (!prevSelectedItems.length) {
          // delay as we are within setState
          setTimeout(() => scrollToSelectedItem(record.id), 200);
        }

        return [record, ...prevSelectedItems];
      });
    } else {
      // remove item from arr
      setSelectedSharesItems((prevSelectedItems) =>
        prevSelectedItems.filter((item) => item.id !== record.id)
      );
    }
  };

  /** Custom table `onSelectAll` callback; handles toggling global selection (cross-folder)  */
  const handleSelectAllShareKeys: Required<SelectedShareKeysProps>['onSelectAllSharedKeys'] = (
    selected,
    rows,
    changedRows
  ) => {
    if (selectedForDetails) setSelectedForDetails(undefined);

    // keep truthy values, because selected rows from other "folders" are included as `undefined` in the list
    const selectedRows = rows.filter(Boolean);

    if (selected) {
      // insert all items at start of arr, filter unique
      setSelectedSharesItems((prevSelectedItems) =>
        uniqBy([...selectedRows, ...prevSelectedItems], 'id')
      );
    } else {
      // use `changedRows`, because `rows` is empty on "deselectAll"
      const rowKeys = changedRows.map((row) => row.id);
      // remove items from arr
      setSelectedSharesItems((prevSelectedItems) =>
        prevSelectedItems.filter((item) => !rowKeys.includes(item.id))
      );
    }
  };

  // --- START FIX: MANUAL TOUCH HANDLING ---
  const timeOutRef = useRef<number>();
  const isTouchMoveRef = useRef(false);
  const lastTapRef = useRef<{ key: string; time: number } | null>(null);

  // ✅ store last touch point so we can open the context menu at correct coords
  const lastTouchPointRef = useRef<{ x: number; y: number } | null>(null);

  // ✅ detect touch / coarse pointer devices (mobile/tablet, including "desktop site" mode)
  const isCoarsePointer =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  const LONG_PRESS_TIME = 600;

  // ✅ open RightClickOverlay the same way as real right-click (contextmenu event)
  const fireSyntheticContextMenu = (target: EventTarget | null) => {
    if (!isCoarsePointer) return;
    const pt = lastTouchPointRef.current;
    if (!pt) return;
    try {
      const el = (target as HTMLElement) || document.body;
      el.dispatchEvent(
        new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          clientX: pt.x,
          clientY: pt.y,
        })
      );
    } catch (e) {
      // ignore
    }
  };

  /** Table `row` callback;
   * @returns object containing event handlers, settings
   */
  const handleRow: Required<FileTableProps>['onRow'] = useCallback(
    (record, index) => {
      if (record.isOnTenderPropertyPanel) {
        return {
          onDoubleClick: () => handleDocumentsFolderDoubleClick(record),
        };
      }

      // Check if this is a classicRegistration without a file
      const isRegistrationWithoutFile =
        (record as any).classicRegistration &&
        !(record as any).classicRegistration?.filename;

      return {
        // ✅ Touch handling for mobile "desktop mode":
        // - isolate events so RightClickOverlay doesn't swallow taps
        // - long-press opens context menu (synthetic `contextmenu`)
        // - tap handled manually (single tap = select, double tap = open/download)
        onTouchStart: (e: any) => {
          if (isCoarsePointer) e.stopPropagation();
          isTouchMoveRef.current = false;
          const t = e?.touches?.[0];
          if (t) {
            lastTouchPointRef.current = { x: t.clientX, y: t.clientY };
          } else {
            lastTouchPointRef.current = null;
          }
          timeOutRef.current = window.setTimeout(() => {
            timeOutRef.current = undefined;
            if (!selectedItems.length) {
              if ((record as any).classicRegistration) {
                // Disable right-click for registrations without files
                if (!isRegistrationWithoutFile) {
                  setRightClickEntity({
                    classicRegistration: (record as any).classicRegistration,
                  });
                  fireSyntheticContextMenu(e?.target);
                }
              } else {
                handleSelectedFileContext(record);
                fireSyntheticContextMenu(e?.target);
              }
            }
          }, LONG_PRESS_TIME);
        },
        onTouchMove: (e: any) => {
          if (isCoarsePointer) e.stopPropagation();
          isTouchMoveRef.current = true;
          if (timeOutRef.current) {
            clearTimeout(timeOutRef.current);
            timeOutRef.current = undefined;
          }
        },
        onTouchEnd: (e: any) => {
          if (isCoarsePointer) e.stopPropagation();
          if (timeOutRef.current) {
            clearTimeout(timeOutRef.current);
            timeOutRef.current = undefined;
          }
          // scroll => ignore tap
          if (isTouchMoveRef.current) return;
          const now = Date.now();
          const lastTap = lastTapRef.current;
          const DOUBLE_TAP_DELAY = 300;
          if (
            lastTap &&
            lastTap.key === record.key &&
            now - lastTap.time < DOUBLE_TAP_DELAY
          ) {
            // DOUBLE TAP -> OPEN
            lastTapRef.current = null;

            // Disable double-click for registrations without files
            if (isRegistrationWithoutFile) {
              return;
            }

            if (record.folder) {
              if (record?.isTenderDocuments) {
                handleDocumentsFolderDoubleClick(record);
              } else {
                handleFolderDoubleClick(record);
              }
            } else {
              handleFileDoubleClick(record);
            }
            return;
          }
          // SINGLE TAP -> SELECT
          lastTapRef.current = { key: record.key, time: now };
          if (e?.ctrlKey || e?.metaKey) {
            if (!!selectedItems.length || !!record)
              setIsUnassignedItemsVisible?.(false);

            return handleSelectCtrl({
              record,
              setSelectedItems,
              selectedItems,
            });
          }
          return handleRowClick(record);
        },
        // Desktop mouse support
        onClick: (event: any) => {
          // isolate from overlay too (safe on desktop)
          event.stopPropagation();
          if (event.ctrlKey || event.metaKey) {
            if (!!selectedItems.length || !!record)
              setIsUnassignedItemsVisible?.(false);

            return handleSelectCtrl({
              record,
              setSelectedItems,
              selectedItems,
            });
          }
          return handleRowClick(record);
        },
        onContextMenu: () => {
          if ((record as any).classicRegistration) {
            // Disable right-click for registrations without files
            if (isRegistrationWithoutFile) return;

            setRightClickEntity({
              classicRegistration: (record as any).classicRegistration,
            });
            return; // Early return after handling classicRegistration
          } else if (
            !selectedItems.length &&
            record &&
            (record.dbFile ||
              record.folder ||
              record.dbFolder ||
              record.dbModuleFile ||
              record.dbAttachment)
          ) {
            handleSelectedFileContext(record);
          }
          // Don't set rightClickEntity if none of the above conditions are met (empty space)
        },
        onDoubleClick: (event: any) => {
          event.stopPropagation();

          // Disable double-click for registrations without files
          if (isRegistrationWithoutFile) {
            return;
          }

          if (record.folder) {
            if (record?.isTenderDocuments) {
              handleDocumentsFolderDoubleClick(record);
            } else {
              handleFolderDoubleClick(record);
            }
          } else {
            handleFileDoubleClick(record);
          }
        },
        // Deleted by task http://jira.detaljen.lan/browse/CPT-33
        // title: record.disabled
        //   ? intl.formatMessage({ id: 'item-unfinished' })
        //   : undefined,
      };
    },
    [
      handleDocumentsFolderDoubleClick,
      selectedItems,
      handleSelectedFileContext,
      setIsUnassignedItemsVisible,
      setSelectedItems,
      handleRowClick,
      handleFolderDoubleClick,
      handleFileDoubleClick,
      isCoarsePointer,
    ]
  );
  // --- END FIX ---

  const tableScroll: Required<FileTableProps>['scroll'] = useMemo(
    () => ({
      // satisfies sum of fixed width columns + reasonable dynamic "name" column
      x: 1065,
      // 141px is topBar + folderNameBar + tableHeader + scrollbar
      y: `calc(100vh - 121px - ${bottomViewHeight}px)`,
    }),
    [bottomViewHeight]
  );

  const renderTenderPropertiesTable = useCallback(
    (props) =>
      renderFile(
        <TenderPropertiesTable
          {...props}
          scroll={tableScroll}
          focusOptions={focusOptions}
          highlightedRow={selectedForDetails}
          selectedKeys={selectedKeys}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          handleRow={handleRow}
          setSelectedItems={setSelectedItems}
          onEmptyOverlay={() => {
            setRightClickEntity({
              file: undefined,
              folder: undefined,
              moduleFile: undefined,
              noFiles: true,
            });
          }}
        />
      ),
    [
      tableScroll,
      focusOptions,
      selectedForDetails,
      handleSelect,
      handleSelectAll,
      handleRow,
      setSelectedItems,
      fileToView,
      selectedUrl,
      svfModal,
      viewerTopMargin,
      bottomViewHeight,
    ]
  );

  const selectedKeys: Required<FileTableProps>['selectedKeys'] = selectedItems.map(
    (item) => item.key
  );

  const selectedSharedKeys: Key[] = selectedSharesItems.map((item) => item.id);

  const handleNavigateToProjectBuilder = () => {
    // Ensure admin mode is active before navigating to project builder
    if (window.sessionStorage.getItem('appState') !== 'superUser') {
      switcher({ theme: themes.su });
      setAppContextState('super-user');
      window.sessionStorage.removeItem('timeMachineDate');
      window.sessionStorage.setItem('appState', 'superUser');
    }
    history.push(`/projects/${projectId}/project-builder`);
  };

  const handleOpenPermissions = () => {
    modals.setFolderRightsModal(true);
  };

  const renderFileTable = (props) =>
    renderFile(
      <FileTable
        {...props}
        scroll={tableScroll}
        focusOptions={focusOptions}
        highlightedRow={selectedForDetails}
        selectedKeys={selectedKeys}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        handleRow={handleRow}
        setSelectedItems={setSelectedItems}
        showInitializeButton={
          window.sessionStorage.getItem('appState') !== 'timeMachine'
        }
        onInitializeFromTemplate={() => setImportTemplateModalVisible(true)}
        onNavigateToProjectBuilder={
          isSuperuser ? handleNavigateToProjectBuilder : undefined
        }
        onOpenPermissions={handleOpenPermissions}
        onEmptyOverlay={() => {
          setRightClickEntity({
            file: undefined,
            folder: undefined,
            moduleFile: undefined,
            noFiles: true,
          });
          setRightClickEntity({
            file: undefined,
            folder: undefined,
            moduleFile: undefined,
            noFiles: true,
          });
        }}
        openSVFTranscribe={(fileToView) => {
          setSVFModal(true);
          setSVFModalFileToView(fileToView);
        }}
      />
    );

  const renderTenderUnassignedFolders = (props) =>
    renderFile(
      <TenderUnassignedFolders
        {...props}
        scroll={tableScroll}
        focusOptions={focusOptions}
        highlightedRow={selectedForDetails}
        selectedKeys={selectedKeys}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        handleRow={handleRow}
        setSelectedItems={setSelectedItems}
        onEmptyOverlay={() => {
          setRightClickEntity({
            file: undefined,
            folder: undefined,
            moduleFile: undefined,
            noFiles: true,
          });
        }}
      />
    );
  const renderTenderTable = (props) => {
    return renderFile(
      <TenderTabs
        {...props}
        perm
        currentPermissionLevel={permissionLevel}
        scroll={tableScroll}
        focusOptions={focusOptions}
        highlightedRow={selectedForDetails}
        selectedSharedKeys={selectedSharedKeys}
        onSelectSharedKeys={handleSelectShareKeys}
        onSelectAllSharedKeys={handleSelectAllShareKeys}
        selectedKeys={selectedKeys}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        handleRow={handleRow}
        handleShareKeyTableRowClick={handleRow}
        setSelectedItems={setSelectedItems}
        bottomViewHeight={bottomViewHeight}
        onEmptyOverlay={() => {
          setRightClickEntity({
            file: undefined,
            folder: undefined,
            moduleFile: undefined,
            noFiles: true,
          });
        }}
        openSVFTranscribe={(fileToView) => {
          setSVFModal(true);
          setSVFModalFileToView(fileToView as any);
        }}
        tenderAttachmentSVFStateById={tenderAttachmentSVFStateById}
      />
    );
  };

  const getModuleUrl = async ({
    projectId,
    moduleFileId,
    moduleId,
    response_type,
  }: GetLinkModuleFileParams) => {
    const url = await getLinkModuleFile({
      moduleFileId,
      projectId,
      moduleId,
      response_type,
    });
    setSelectedUrl(url);
    return url;
  };

  const isGetTenderAttachmentResponse = (
    obj: any
  ): obj is GetTenderAttachmentResponse => {
    return (
      obj &&
      obj.location !== undefined &&
      obj.title !== undefined &&
      obj.module === undefined
    );
  };

  let panelNode: React.ReactElement | null | undefined;

  if (isOnDetails || isOnContract || isOnQA || isOnOffers) {
    panelNode = null;
  }
  // COMMENTED OUT: Empty state now handled in FileTable
  // else if (
  //   isSuperuser &&
  //   isProjectEmpty &&
  //   window.sessionStorage.getItem('appState') !== 'timeMachine'
  // ) {
  //   panelNode = <EmptyProjectPannel />;
  // }
  else if (uploadContext.length && (isOnArchive || isOnTender)) {
    panelNode = <UploadPanel key="upload" />;
  } else if (selectedItems.length) {
    panelNode = (
      <CherryPickPanel
        key="cherry-picker"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    );
  } else if (isUnassignedItemsVisible) {
    panelNode = (
      <PropertiesPanel
        tenderPropertiesTable={renderTenderPropertiesTable}
        key="unassigned-properties"
        selectedItem={selectedForDetails}
      />
    );
  } else if (selectedForDetails) {
    panelNode = (
      <PropertiesPanel
        tenderPropertiesTable={renderTenderPropertiesTable}
        key="properties"
        selectedItem={selectedForDetails}
      />
    );
  }

  // attach resize observer on zoneDiv
  useEffect(() => {
    const div = zoneDivRef.current;

    if (div) {
      const resizeObserver = resizeObserverRef.current; // ResizeObserver instance
      resizeObserver.observe(div); // add listener

      // cleanup -> remove listener
      return () => resizeObserver.unobserve(div);
    }
    // }, [panelNode?.key]);
  }, []);

  useEffect(() => {
    if (
      !isGetTenderAttachmentResponse(fileToView) &&
      fileToView &&
      (fileToView.hasOwnProperty('versions') ||
        fileToView.hasOwnProperty('location'))
    ) {
      // Always update fileRevision and proceed with fetching location,
      // even if it’s the same as before.
      setFileRevision({
        id: fileVersionsContext[fileToView.id]?.id,
        revision: fileVersionsContext[fileToView.id]?.revision,
      });

      const selectedIndex = fileVersionsContext[fileToView.id]
        ? fileToView.versions?.findIndex(
            (opt) => opt.id === fileVersionsContext[fileToView.id]?.id
          )
        : 0;

      if (fileToView.hasOwnProperty('location')) {
        getModuleUrl({
          moduleFileId: String(fileToView.id),
          projectId: String(projectId),
          moduleId: (fileToView as DbModuleFile).module,
          response_type: 'base64',
        });
      } else if ((selectedIndex as number) >= 0) {
        const getFileToViewLocation = async () => {
          if (fileToView.versions) {
            const getFileLocation = await requestFileLocation({
              fileId: fileToView.versions[selectedIndex as number].id,
              projectId:
                fileToView.versions[selectedIndex as number].project_id,
              versionedFileId:
                fileToView.versions[selectedIndex as number].versioned_file_id,
            });
            return getFileLocation;
          }
        };
        const fileLocation = getFileToViewLocation();
        const setFileLocation = async () => {
          setSelectedUrl(await fileLocation);
        };
        setFileLocation();
      }
    } else if (isGetTenderAttachmentResponse(fileToView)) {
      setSelectedUrl(fileToView.location);
    } else {
      setSelectedUrl(undefined);
    }
  }, [fileToView, fileVersionsContext]);

  const renderFile = (content: JSX.Element | null) => {
    if (content === null) return null;

    const isTenderRootSVFViewerSource = isTenderRootSVFSource(fileToView);
    const isTenderFolderSVFViewerSource =
      !!fileToView &&
      (fileToView as any)?.sourceType === 'tender-admin-inside-svf';

    const isUnassignedTenderFolderSVFViewerSource =
      !!fileToView &&
      (fileToView as any)?.sourceType === 'tender-unassigned-inside-svf';

    if (
      fileToView &&
      (isTenderRootSVFViewerSource ||
        isTenderFolderSVFViewerSource ||
        isUnassignedTenderFolderSVFViewerSource ||
        (!isGetTenderAttachmentResponse(fileToView) && fileToView?.versions) ||
        (fileToView as DbModuleFile).location ||
        isGetTenderAttachmentResponse(fileToView)) &&
      (isTenderRootSVFViewerSource ||
        isTenderFolderSVFViewerSource ||
        isUnassignedTenderFolderSVFViewerSource ||
        selectedUrl) &&
      !svfModal
    ) {
      const { extension } = parseFileName(
        fileToView?.['title']
          ? fileToView?.['title']
          : fileToView?.['file_name']
      );

      return (
        <RightSideViewerContainer
          bottomViewHeight={bottomViewHeight}
          MainContent={content}
          ViewerItem={
            <Viewer
              fileToView={fileToView as any}
              onCloseSVFViewer={() => setFileToView(undefined)}
              fileUrl={selectedUrl || ''}
              fileExtension={extension}
            />
          }
          isViewerLoading={
            isTenderRootSVFViewerSource ||
            isTenderFolderSVFViewerSource ||
            isUnassignedTenderFolderSVFViewerSource
              ? false
              : !selectedUrl
          }
          viewerTopMargin={viewerTopMargin}
        />
      );
    }

    return content;
  };

  const getRightClickItems = () => {
    const isUnassigned =
      history.location.pathname.includes('/tender-folders/') &&
      !history.location.pathname.includes('/tenders/');

    if (rightClickEntity.classicRegistration) {
      return dataContextMenu.moduleFile;
    } else if (rightClickEntity.moduleFile) {
      return dataContextMenu.moduleFile;
    } else if (rightClickEntity.folder) {
      if (isUnassigned) {
        return dataContextMenu.folder.filter(
          (item) => item.name === 'download'
        );
      }
      return window.sessionStorage.getItem('appState') === 'timeMachine'
        ? dataContextMenu.folder.filter((item) => item.name === 'download')
        : dataContextMenu.folder;
    } else if (rightClickEntity.tenderFolder) {
      return dataContextMenu.folder.filter((item) => item.name === 'download');
    } else if (rightClickEntity.file) {
      if (isUnassigned) {
        return dataContextMenu.file.filter((item) => item.name === 'download');
      }
      return window.sessionStorage.getItem('appState') === 'timeMachine'
        ? dataContextMenu.file.filter(
            (item) => item.name === 'download' || item.name === 'svf-view-file'
          )
        : dataContextMenu.file;
    } else if (rightClickEntity.attachment) {
      if (rightClickEntity.attachment.state === DB_STATE.REMOVED) {
        return dataContextMenu.deletedAttachment;
      }
      const base =
        window.sessionStorage.getItem('appState') === 'timeMachine'
          ? dataContextMenu.attachment.filter(
              (item) => item.name === 'download'
            )
          : dataContextMenu.attachment;

      // isFirstLevel === true -> show everything (edit, delete, download)
      // isFirstLevel === false -> show only download
      return isFirstLevel
        ? base
        : base.filter(
            (it) => it.name !== 'edit-attachment' && it.name !== 'delete'
          );
    } else if (
      rightClickEntity.noFiles &&
      window.sessionStorage.getItem('appState') !== 'timeMachine'
    ) {
      return dataContextMenu.noItemSelected;
    }

    return [];
  };

  return (
    <div
      css={style.container}
      onContextMenu={(e) => {
        e.preventDefault();
      }}>
      {/* <div css={style.container}>domEvent */}
      <RightClickOverlay
        permissionLevel={rightClickEntityParentPermission}
        items={getRightClickItems()}
        disabled={!!selectedItems.length}
        onItemClick={(item, option) => {
          rightClickAction(item.modal, option);
          setTimeout(() => {
            setRightClickEntityOverlay({
              folder: undefined,
              file: undefined,
              moduleFile: undefined,
              noFiles: undefined,
            });
            // setRightClickEntity({
            //   folder: undefined,
            //   file: undefined,
            //   moduleFile: undefined,
            //   noFiles: undefined,
            // });
          }, 1000);
        }}
        onOverlayClose={() => {
          if (
            rightClickEntity.file ||
            rightClickEntity.attachment ||
            rightClickEntity.deletedAttachment ||
            rightClickEntity.folder ||
            rightClickEntity.moduleFile
          )
            setTimeout(() => {
              setRightClickEntity({
                folder: undefined,
                file: undefined,
                moduleFile: undefined,
                attachment: undefined,
                deletedAttachment: undefined,
                noFiles: undefined,
              });
            }, 1000);
        }}
        selected={rightClickEntity}
        focusOptions={focusOptions}>
        <div
          onClick={(e) => {
            if (isCoarsePointer) e.stopPropagation();
          }}
          onDoubleClick={(e) => {
            if (isCoarsePointer) e.stopPropagation();
          }}
          onTouchStart={(e) => {
            if (isCoarsePointer) e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            if (isCoarsePointer) e.stopPropagation();
          }}>
          <Switch>
            <Route
              path="/projects/:projectId/tender-folders/:folderId"
              render={renderTenderUnassignedFolders}
            />
            <Route
              path="/projects/:projectId/archive/:folderId"
              render={renderFileTable}
            />

            <Route
              path="/projects/:projectId/archive"
              render={renderFileTable}
            />

            <Route
              path="/projects/:projectId/tenders/:tenderId"
              render={renderTenderTable}
            />

            <Route
              path="/projects/:projectId/tenders"
              render={renderTenderTable}
            />

            <Route
              path="/projects/:projectId/recents"
              render={(props) =>
                renderFile(
                  <RecentsTable
                    {...props}
                    scroll={tableScroll}
                    highlightedRow={selectedForDetails}
                    selectedKeys={selectedKeys}
                    onSelect={handleSelect}
                    handleRow={handleRow}
                    setSelectedItems={setSelectedItems}
                    openSVFTranscribe={(fileToView) => {
                      setSVFModal(true);
                      setSVFModalFileToView(fileToView);
                    }}
                  />
                )
              }
            />

            <Route
              path="/projects/:projectId/favourites"
              render={(props) =>
                renderFile(
                  <FavouritesTable
                    {...props}
                    scroll={tableScroll}
                    highlightedRow={selectedForDetails}
                    selectedKeys={selectedKeys}
                    onSelect={handleSelect}
                    handleRow={handleRow}
                    setSelectedItems={setSelectedItems}
                    focusOptions={focusOptions}
                    openSVFTranscribe={(fileToView) => {
                      setSVFModal(true);
                      setSVFModalFileToView(fileToView);
                    }}
                  />
                )
              }
            />

            {/* PRESENT IN STAGE but NOT IN DEV */}
            {/*<Route*/}
            {/*  path="/projects/:projectId/share"*/}
            {/*  render={(props) =>*/}
            {/*    renderFile(*/}
            {/*      <SharedTable*/}
            {/*        {...props}*/}
            {/*        scroll={tableScroll}*/}
            {/*        highlightedRow={selectedForDetails}*/}
            {/*        selectedKeys={selectedKeys}*/}
            {/*        onSelect={handleSelect}*/}
            {/*        onSelectAll={handleSelectAll}*/}
            {/*        handleRow={handleRow}*/}
            {/*        setSelectedItems={setSelectedItems}*/}
            {/*        openSVFTranscribe={(fileToView) => {*/}
            {/*          setSVFModal(true);*/}
            {/*          setSVFModalFileToView(fileToView);*/}
            {/*        }}*/}
            {/*      />*/}
            {/*    )*/}
            {/*  }*/}
            {/*/>*/}

            <Route
              path="/projects/:projectId/search"
              render={(props) =>
                renderFile(
                  <SearchTable
                    {...props}
                    scroll={tableScroll}
                    highlightedRow={selectedForDetails}
                    selectedKeys={selectedKeys}
                    onSelect={handleSelect}
                    handleRow={handleRow}
                    setSelectedItems={setSelectedItems}
                    openSVFTranscribe={(fileToView) => {
                      setSVFModal(true);
                      setSVFModalFileToView(fileToView);
                    }}
                  />
                )
              }
            />

            <Route
              path="/projects/:projectId/module/:moduleId"
              render={(props) => {
                const { moduleId } = props.match.params as { moduleId: string };

                return renderFile(
                  isModuleAdminLoading ? null : isModuleAdmin ? (
                    <SectorRegistrationsTable
                      moduleId={Number(moduleId)}
                      scroll={tableScroll}
                      highlightedRow={selectedForDetails}
                      selectedKeys={selectedKeys}
                      onSelect={handleSelect}
                      onSelectAll={handleSelectAll}
                      onRow={handleRow}
                    />
                  ) : (
                    <ModuleTable
                      {...props}
                      scroll={tableScroll}
                      highlightedRow={selectedForDetails}
                      selectedKeys={selectedKeys}
                      onSelect={handleSelect}
                      onSelectAll={handleSelectAll}
                      onRow={handleRow}
                      openSVFTranscribe={(fileToView) => {
                        setSVFModal(true);
                        setSVFModalFileToView(fileToView);
                      }}
                    />
                  )
                );
              }}
            />

            {/* SHELVED: sectors table view implementation
            <Route
              path="/projects/:projectId/sectors"
              render={(props) =>
                renderFile(
                  <SectorsBrowserView {...props} scroll={tableScroll} />
                )
              }
            />
            */}

            {/* Modules registrations - single route */}
            <Route
              path="/projects/:projectId/section-modules/sector/:sectorId/registrations"
              render={(props) => {
                const { sectorId } = props.match.params as { sectorId: string };
                return renderFile(
                  <SectorRegistrationsTable
                    sectorId={sectorId}
                    scroll={tableScroll}
                    highlightedRow={selectedForDetails}
                    selectedKeys={selectedKeys}
                    onSelect={handleSelect}
                    onSelectAll={handleSelectAll}
                    onRow={handleRow}
                  />
                );
              }}
            />

            <Redirect to={`/projects/${projectId}`} />
          </Switch>
        </div>
      </RightClickOverlay>

      <div css={style.panelsContainer} ref={zoneDivRef}>
        {/* panel bellow the table e.g. UploadPanel */}
        {panelNode}
      </div>

      {/* MODALS */}
      <FileProgressPanelDownload />
      <EditFileModal
        visible={modals.fileEditModal}
        file={rightClickEntity.file}
        onCancel={() => {
          modals.setFileEditModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
      />
      <DeleteModal
        visible={modals.deleteModal}
        files={rightClickEntity.file ? [rightClickEntity.file] : undefined}
        folders={
          rightClickEntity.folder ? [rightClickEntity.folder] : undefined
        }
        attachments={
          rightClickEntity.attachment
            ? [rightClickEntity.attachment]
            : undefined
        }
        onCancel={() => modals.setDeleteModal(false)}
        onDone={() => {
          modals.setDeleteModal(false);
          setSelectedForDetails(undefined);
        }}
      />
      <RestoreAttachments
        visible={modals.restoreToModal}
        attachments={
          rightClickEntity.attachment
            ? [rightClickEntity.attachment]
            : undefined
        }
        onCancel={() => modals.setRestoreToModal(false)}
        onDone={() => {
          modals.setRestoreToModal(false);
          setSelectedForDetails(undefined);
        }}
      />
      <MoveModal
        visible={modals.moveModal}
        folderId={folderId}
        files={rightClickEntity.file ? [rightClickEntity.file] : undefined}
        folders={
          rightClickEntity.folder ? [rightClickEntity.folder] : undefined
        }
        onCancel={() => modals.setMoveModal(false)}
        onDone={() => {
          modals.setMoveModal(false);
          setSelectedForDetails(undefined);
        }}
      />

      {/* TODO: maybe move these up, to be shared with LeftSideView/Nav etc. */}
      <CreateFolderModal
        visible={modals.createFolderModal}
        parent={currentFolder}
        onCancel={() => modals.setCreateFolderModal(false)}
      />

      <ShareModal
        visible={modals.shareModal}
        files={rightClickEntity.file ? [rightClickEntity.file] : []}
        onCancel={() => {
          modals.setShareModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        onDone={() => {
          modals.setShareModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
      />

      <CorrelationLetterModal
        visible={modals.correlationLetterModal}
        selectedMovements={
          rightClickEntity.movement ? [rightClickEntity.movement] : []
        }
        onCancel={() => modals.setCorrelationLetterModal(false)}
      />

      <EditFolderModal
        visible={modals.folderEditModal}
        folder={rightClickEntity.folder}
        onCancel={() => {
          modals.setFolderEditModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
      />
      <RightsModal
        visible={modals.folderRightsModal}
        onCancel={() => {
          modals.setFolderRightsModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        folder={rightClickEntity.folder}
      />

      <FolderPropertiesModal
        visible={modals.folderPropertiesModal}
        onCancel={() => {
          modals.setFolderPropertiesModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        folder={rightClickEntity.folder}
      />

      <ItemLogModal
        key={
          rightClickEntity.file?.id ||
          rightClickEntity.folder?.id ||
          'log-modal-default'
        }
        visible={modals.itemLogModal}
        onCancel={() => {
          modals.setItemLogModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        file={rightClickEntity.file}
        folder={rightClickEntity.folder}
      />

      <Upload multiple beforeUpload={() => false} onChange={handleUploadChange}>
        <span ref={uploadRef} />
      </Upload>

      <EditAttachmentModal
        visible={modals.attachmentEditModal}
        attachment={rightClickEntity.attachment}
        onCancel={() => {
          modals.setAttachmentEditModal(false);
          setRightClickEntity({
            attachment: undefined,
            file: undefined,
            folder: undefined,
          });
        }}
        onDone={() => {
          modals.setAttachmentEditModal(false);
          setRightClickEntity({
            attachment: undefined,
            file: undefined,
            folder: undefined,
          });
        }}
      />

      <SVFViewerModal
        visible={svfModal}
        fileToView={svfModelFileToView ?? rightClickEntity.file}
        forceViewer={svfModalForceViewer}
        onCancel={() => {
          invalidateCurrentQueries();

          setSVFModalForceViewer(false);
          setSVFModal(false);
          setSVFModalFileToView(undefined);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        onAttachmentStateChange={handleTenderAttachmentSVFStateChange}
        onReloadData={(close) => {
          // ==called when modal flow finishes or closes
          // For archive/versioned files we just invalidate queries.
          // For root tender attachments we also persist the latest local modal state
          // back into RightSideView, because they do not live in FileVersionsContext.
          invalidateCurrentQueries();

          if (
            isTenderRootSVFSource(svfModelFileToView) &&
            svfModelFileToView?.id
          ) {
            const fileGuid = String(svfModelFileToView.id);
            const finalState = getTenderRootAttachmentFinalState(
              svfModelFileToView
            );

            if (typeof finalState === 'number') {
              handleTenderAttachmentSVFStateChange(fileGuid, finalState);
            }
          }

          if (close) {
            setSVFModal(false);
            setSVFModalForceViewer(false);
            setSVFModalFileToView(undefined);
          }
        }}
        onOpenWindowed={() => {
          // ==move successful modal result into right-side windowed viewer
          // Root tender attachments need the latest bridged state injected manually,
          // because Viewer reads it directly from the passed object.
          const nextFileToView = isTenderRootSVFSource(svfModelFileToView)
            ? {
                ...(svfModelFileToView as any),
                extension_svf_model: {
                  ...((svfModelFileToView as any)?.extension_svf_model || {}),
                  state:
                    getTenderRootAttachmentFinalState(svfModelFileToView) ?? 7,
                },
              }
            : rightClickEntity.file
            ? rightClickEntity.file
            : svfModelFileToView;

          setFileToView(nextFileToView as any);
          setSVFModal(false);
          setSVFModalForceViewer(false);
        }}
        onOpenFullscreen={() => {
          setSVFModal(true);
          setSVFModalForceViewer(true);
        }}
      />

      <ImportTemplateModal
        visible={importTemplateModalVisible}
        onCancel={() => setImportTemplateModalVisible(false)}
        mode="archive"
      />

      {window.sessionStorage.getItem('appState') !== 'timeMachine' &&
        showUploadZone &&
        (isOnArchive || isTenderDocumentsRoot) && (
          <UploadDropZonePannel
            size="large"
            disableFolderUpload={
              isOnArchive
                ? !isSuperuser &&
                  folderId !== null &&
                  permissionLevel < RIGHTS_LEVEL.WRITE
                : true // tender root folder upload disabled
            }
            disableFileUpload={
              isOnArchive
                ? folderId === null // archive root - blocked
                : !isSuperuser && permissionLevel < RIGHTS_LEVEL.WRITE // tender documents root - allowed
            }
            onDismiss={() => {
              setShowUploadZone(false);
            }}
          />
        )}
    </div>
  );
};

export default RightSideView;
